import { create } from 'zustand';
import type { AlertType, TicketStatus } from '@asi/shared';
import { supabase } from '@/services/supabase';
import { getCidDescription } from '@/services/arc';
import { geolocationService, type GeoPosition } from '@/services/geolocation';
import { playNewAlertSound } from '@/services/alert-sound';
import { notifyStatusChange } from '@/services/status-notifier';
import { pushAlertToast } from '@/stores/toast-store';

// Track alert IDs we have already announced so the chime fires only once
// per new alert (and not on the first load of the dashboard).
const announcedAlertIds = new Set<string>();
let announcedSeeded = false;
// Tracks the last-known status of each alert across `fetchActiveAlerts`
// invocations so we can detect transitions (e.g. PENDING -> ASSIGNED) and
// raise a chime + desktop notification for the operator. Reset on logout.
const lastKnownStatus = new Map<string, string>();

// Maximum age (ms) of `client_locations.updated_at` / `patrol_locations.updated_at`
// before we consider the device OFFLINE regardless of the stored status. Mobile
// apps only flip the column to OFFLINE on a clean logout — process kills, OS
// background restrictions, network drops, etc. leave the row stuck on
// ONLINE/AVAILABLE forever. Mobile heartbeats are 30s (patrol) and 60s (client),
// so 180s tolerates 3-6 missed writes before showing the operator OFFLINE.
const PRESENCE_FRESHNESS_MS = 180_000;

const ALERT_TYPE_NAMES: Record<string, string> = {
  SOS:       'SOS',
  PANIC:     'Pánico',
  FIRE:      'Incendio',
  MEDICAL:   'Médica',
  INTRUSION: 'Intrusión',
  CALL:      'Llamada',
};

// =====================================================
// MONITORING CENTER (ARC) EMERGENCY STORE
// For operators watching real-time alerts & patrols
// =====================================================

export interface Alert {
  id: string;
  ticket_number: string;
  type: AlertType;
  status: TicketStatus;
  latitude: number;
  longitude: number;
  address?: string;
  description?: string;
  client_id: string | null;
  client_name?: string;
  client_phone?: string;
  patrol_id?: string;
  patrol_name?: string;
  created_at: string;
  updated_at: string;
  // Panel-originated tickets (panic, fire, intrusion from a HPP alarm panel)
  // are linked to a site/account instead of a mobile client. The operator
  // panel needs the cuenta name + number + verbal password (clave) to
  // verify identity over the phone before dispatching, so we surface them
  // alongside the alert. All optional — mobile-client SOS alerts won't have
  // these populated.
  site_id?: string | null;
  site_name?: string | null;
  site_address?: string | null;
  account_id?: string | null;
  account_number?: string | null;
  account_name?: string | null;
  account_clave?: string | null;
  source_arc_event_id?: string | null;
}

export interface Patrol {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  status: 'AVAILABLE' | 'BACKGROUND' | 'ASSIGNED' | 'EN_ROUTE' | 'ON_SCENE' | 'OFFLINE';
  phone?: string;
  marker_icon: string;
  last_update: string;
}

export interface ClientLive {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  status: 'ONLINE' | 'BACKGROUND' | 'OFFLINE';
  phone?: string;
  marker_icon: string;
  battery_level: number | null;
  is_charging: boolean | null;
  accuracy: number | null;
  speed: number | null;
  last_update: string;
}

export interface ClientAccountInfo {
  id: string;
  account_number: string;
  name: string;
  type: string | null;
  phone: string | null;
  billing_address: string | null;
  city: string | null;
  role: string;
  client_tier: string | null;
  created_at: string | null;
}

export interface ClientDetails {
  open_alerts: number;
  accounts: ClientAccountInfo[];
}

// ─── Alarm-panel sites & ARC events ─────────────────────────────────────────
// A "site" here is a customer location that has at least one alarm panel.
// Sites carry lat/lng so we render them on the operator map alongside live
// people. Their colour reflects the most severe currently-open arc_event so
// the operator can see at a glance which addresses need attention.

export type SiteSeverity = 'idle' | 'info' | 'warning' | 'alarm' | 'critical' | 'dispatched';

export interface SiteMarker {
  id: string;
  name: string;
  address: string | null;
  account_number: string | null;
  account_id: string | null;
  account_name: string | null;
  account_phone: string | null;
  account_type: string | null;
  account_city: string | null;
  account_tier: string | null;
  account_created_at: string | null;
  latitude: number;
  longitude: number;
  // Aggregates derived in the store from arcEvents + active tickets
  open_events_count: number;
  max_severity: SiteSeverity;
  has_dispatched_ticket: boolean;
}

export interface ArcEventLite {
  id: string;
  site_id: string | null;
  panel_id: string | null;
  cid_code: string;
  cid_qualifier: 'E' | 'R';
  zone_no: number | null;
  zone_name: string | null;
  severity: 'INFO' | 'WARNING' | 'ALARM' | 'CRITICAL';
  occurred_at: string;
  received_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  ticket_id: string | null;
  // From cid_codes join (denormalised at read time)
  description_es: string | null;
  auto_dispatch: boolean;
  // From alarm_sites join
  site_name: string | null;
  site_address: string | null;
  site_lat: number | null;
  site_lng: number | null;
  account_id: string | null;
  account_number: string | null;
  account_tier: string | null;
  account_created_at: string | null;
}

interface EmergencyState {
  // Map center (Guatemala City by default)
  mapCenter: GeoPosition;

  // All active alerts for the ARC
  activeAlerts: Alert[];
  alertsLoading: boolean;

  // Selected alert for detail view
  selectedAlert: Alert | null;

  // All patrols
  patrols: Patrol[];
  patrolsLoading: boolean;

  // Selected patrol
  selectedPatrol: Patrol | null;

  // Live clients (mobile-client running with background tracking)
  clients: ClientLive[];
  clientsLoading: boolean;
  selectedClient: ClientLive | null;
  selectedClientDetails: ClientDetails | null;
  selectedClientDetailsLoading: boolean;

  // Alarm-panel sites and their open ARC events
  sites: SiteMarker[];
  sitesLoading: boolean;
  selectedSite: SiteMarker | null;
  arcEvents: ArcEventLite[];
  arcEventsLoading: boolean;
  selectedArcEvent: ArcEventLite | null;

  // Realtime subscription cleanup
  unsubscribe: (() => void) | null;

  // Actions
  initializeLocation: () => void;
  fetchActiveAlerts: (silent?: boolean) => Promise<void>;
  fetchPatrols: (silent?: boolean) => Promise<void>;
  fetchClients: (silent?: boolean) => Promise<void>;
  fetchSitesAndEvents: (silent?: boolean) => Promise<void>;
  selectAlert: (alert: Alert | null) => void;
  selectPatrol: (patrol: Patrol | null) => void;
  selectClient: (client: ClientLive | null) => void;
  selectSite: (site: SiteMarker | null) => void;
  selectArcEvent: (event: ArcEventLite | null) => void;
  acknowledgeArcEventInStore: (eventId: string) => Promise<void>;
  resolveArcEventInStore: (eventId: string, resolution: string) => Promise<void>;
  dispatchPatrolForArcEvent: (eventId: string) => Promise<{ success: boolean; reason?: string; billed?: boolean; invoiceNumber?: string; amount?: number }>;
  assignPatrolToAlert: (alertId: string, patrolId: string) => Promise<void>;
  updateAlertStatus: (alertId: string, status: TicketStatus, notes?: string) => Promise<void>;
  bulkUpdateAlertStatus: (alertIds: string[], status: TicketStatus, notes?: string) => Promise<{ updated: number }>;
  subscribeToRealtime: () => void;
  reset: () => void;
}

// Guatemala City center
const GUATEMALA_CENTER: GeoPosition = {
  latitude: 14.6349,
  longitude: -90.5069,
  accuracy: 1000,
  timestamp: Date.now(),
};

export const useEmergencyStore = create<EmergencyState>((set, get) => ({
  // Initial state
  mapCenter: GUATEMALA_CENTER,
  activeAlerts: [],
  alertsLoading: false,
  selectedAlert: null,
  patrols: [],
  patrolsLoading: false,
  selectedPatrol: null,
  clients: [],
  clientsLoading: false,
  selectedClient: null,
  selectedClientDetails: null,
  selectedClientDetailsLoading: false,
  unsubscribe: null,
  sites: [],
  sitesLoading: false,
  selectedSite: null,
  arcEvents: [],
  arcEventsLoading: false,
  selectedArcEvent: null,

  initializeLocation: () => {
    // For ARC, center on Guatemala City.
    // NOTE: subscribeToRealtime() is NOT called here intentionally.
    // The Realtime WebSocket must be opened after the auth session is loaded,
    // otherwise it uses the anon JWT and RLS blocks postgres_changes events.
    // EmergencyPage calls subscribeToRealtime() explicitly after the first
    // successful fetchActiveAlerts() which confirms auth is ready.
    set({ mapCenter: GUATEMALA_CENTER });
  },

  fetchActiveAlerts: async (silent = false) => {
    if (!silent) set({ alertsLoading: true });
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          id,
          ticket_number,
          type,
          status,
          lat,
          lng,
          address,
          description,
          client_id,
          patrol_id,
          site_id,
          account_id,
          source_arc_event_id,
          created_at,
          updated_at,
          client:profiles!tickets_client_id_fkey(full_name, phone),
          patrol:profiles!tickets_patrol_id_fkey(full_name),
          site:alarm_sites!tickets_site_id_fkey(name, address, clave),
          account:accounts!tickets_account_id_fkey(account_number, name)
        `)
        .in('status', ['PENDING', 'ASSIGNED', 'IN_PROGRESS', 'EN_ROUTE', 'ON_SCENE'])
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const alerts: Alert[] = (data || []).map((t) => {
        const site = t.site as { name?: string; address?: string; clave?: string } | null;
        const account = t.account as { account_number?: string; name?: string } | null;
        return {
          id: t.id,
          ticket_number: t.ticket_number,
          type: t.type,
          status: t.status,
          latitude: (t.lat as number) ?? 0,
          longitude: (t.lng as number) ?? 0,
          address: t.address ?? undefined,
          description: t.description ?? undefined,
          client_id: t.client_id,
          client_name: (t.client as { full_name?: string })?.full_name,
          client_phone: (t.client as { phone?: string })?.phone,
          patrol_id: t.patrol_id ?? undefined,
          patrol_name: (t.patrol as { full_name?: string })?.full_name,
          created_at: t.created_at,
          updated_at: t.updated_at,
          site_id: t.site_id ?? null,
          site_name: site?.name ?? null,
          site_address: site?.address ?? null,
          account_id: t.account_id ?? null,
          account_number: account?.account_number ?? null,
          account_name: account?.name ?? null,
          account_clave: site?.clave ?? null,
          source_arc_event_id: t.source_arc_event_id ?? null,
        };
      });

      set({ activeAlerts: alerts, alertsLoading: false });

      // Play chime for any newly arrived PENDING alert. Skip the very first
      // load (silent === false on initial fetch) so we don't blast the
      // operator the moment they open the dashboard.
      if (!announcedSeeded) {
        alerts.forEach((a) => {
          announcedAlertIds.add(a.id);
          lastKnownStatus.set(a.id, a.status);
        });
        announcedSeeded = true;
      } else {
        const pendingNew = alerts.filter(
          (a) => a.status === 'PENDING' && !announcedAlertIds.has(a.id)
        );
        if (pendingNew.length > 0) {
          pendingNew.forEach((a) => {
            announcedAlertIds.add(a.id);
            const typeLabel = ALERT_TYPE_NAMES[a.type] ?? a.type;
            pushAlertToast({
              kind: 'new_alert',
              title: `🚨 Nueva alerta: ${typeLabel}`,
              body: a.client_name ?? 'Cliente desconocido',
            });
          });
          // Play the type-specific sound for the newest pending alert so the
          // operator can identify the alert kind without looking. If the
          // batch contains multiple types we still only chime once to avoid
          // overlapping audio.
          const newest = pendingNew[pendingNew.length - 1];
          playNewAlertSound(newest.type);
        }

        // Detect status transitions on alerts already known. We notify the
        // operator with a chime + in-app notification so they don't have
        // to stare at the dashboard to see the patrol moving through the
        // workflow (PENDING -> ASSIGNED -> EN_ROUTE -> ON_SCENE -> ...).
        alerts.forEach((a) => {
          const prev = lastKnownStatus.get(a.id);
          if (prev && prev !== a.status) {
            notifyStatusChange({
              ticketNumber: a.ticket_number,
              clientName: a.client_name,
              patrolName: a.patrol_name,
              from: prev as never,
              to: a.status,
            });
            // In-app toast for the ASSIGNED transition (most critical one).
            if (prev === 'PENDING' && a.status === 'ASSIGNED') {
              pushAlertToast({
                kind: 'assigned',
                title: `✅ Alerta #${a.ticket_number} asignada`,
                body: a.patrol_name
                  ? `${a.client_name ?? 'Cliente'} → ${a.patrol_name}`
                  : `Cliente: ${a.client_name ?? 'desconocido'}`,
              });
            }
          }
          lastKnownStatus.set(a.id, a.status);
        });

        // Make sure we don't grow the set forever
        const currentIds = new Set(alerts.map((a) => a.id));
        announcedAlertIds.forEach((id) => {
          if (!currentIds.has(id)) announcedAlertIds.delete(id);
        });
        // Drop status memory for alerts that are no longer active so a
        // re-opened ticket fires fresh transitions.
        Array.from(lastKnownStatus.keys()).forEach((id) => {
          if (!currentIds.has(id)) lastKnownStatus.delete(id);
        });
      }

      // Update selected alert if it exists
      const { selectedAlert } = get();
      if (selectedAlert) {
        const updated = alerts.find((a) => a.id === selectedAlert.id);
        set({ selectedAlert: updated ?? null });
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
      set({ alertsLoading: false });
    }
  },

  fetchPatrols: async (silent = false) => {
    if (!silent) set({ patrolsLoading: true });
    try {
      // Two-step fetch so the assign-patrol dropdown can list every active
      // patroller, even those whose mobile app has not yet reported its first
      // GPS fix (otherwise patrol_locations would be empty for them and they
      // would silently disappear from the operator UI). We also keep OFFLINE
      // patrols visible — the operator decides whether to assign or call.
      const [{ data: profilesData }, { data: locsData, error: locsErr }] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, full_name, phone, marker_icon')
          .eq('role', 'patrol')
          .eq('is_active', true),
        supabase
          .from('patrol_locations')
          .select('patrol_id, lat, lng, status, updated_at')
          .order('updated_at', { ascending: false }),
      ]);

      if (locsErr) throw locsErr;

      const locByPatrol = new Map<string, { lat: number; lng: number; status: string; updated_at: string }>();
      (locsData ?? []).forEach((row) => {
        locByPatrol.set(row.patrol_id as string, {
          lat: (row.lat as number) ?? 0,
          lng: (row.lng as number) ?? 0,
          status: (row.status as string) ?? 'OFFLINE',
          updated_at: row.updated_at as string,
        });
      });

      // Staleness override: if the row hasn't been refreshed within
      // PRESENCE_FRESHNESS_MS we treat the patrol as OFFLINE regardless
      // of the last stored status. The mobile app only writes OFFLINE on a
      // clean logout — if the process is killed, network drops, or the OS
      // background-restricts the app, the row stays AVAILABLE forever and
      // the operator sees a phantom online patrol.
      const now = Date.now();
      const patrols: Patrol[] = (profilesData ?? []).map((p) => {
        const loc = locByPatrol.get(p.id as string);
        const updatedAt = loc?.updated_at ?? new Date(0).toISOString();
        const ageMs = now - new Date(updatedAt).getTime();
        const stale = ageMs > PRESENCE_FRESHNESS_MS;
        const storedStatus = (loc?.status as Patrol['status']) ?? 'OFFLINE';
        return {
          id: p.id as string,
          name: (p.full_name as string) ?? 'Sin nombre',
          latitude: loc?.lat ?? 0,
          longitude: loc?.lng ?? 0,
          status: stale ? 'OFFLINE' : storedStatus,
          phone: (p.phone as string | null) ?? undefined,
          marker_icon: ((p as { marker_icon?: string }).marker_icon as string) ?? 'motorcycle',
          last_update: updatedAt,
        };
      });

      set({ patrols, patrolsLoading: false });
    } catch (error) {
      console.error('Error fetching patrols:', error);
      set({ patrolsLoading: false });
    }
  },

  selectAlert: (alert) => {
    set({ selectedAlert: alert, selectedPatrol: null, selectedClient: null, selectedSite: null, selectedArcEvent: null });
  },

  selectPatrol: (patrol) => {
    set({ selectedPatrol: patrol, selectedAlert: null, selectedClient: null, selectedSite: null, selectedArcEvent: null });
  },

  selectClient: (client) => {
    set({
      selectedClient: client,
      selectedAlert: null,
      selectedPatrol: null,
      selectedClientDetails: null,
      selectedClientDetailsLoading: !!client,
    });
    if (!client) return;
    // Fire-and-forget: enrich with account memberships + open alerts so the
    // operator can ID the customer fast without leaving the map.
    (async () => {
      try {
        const [memberships, openAlerts] = await Promise.all([
          supabase
            .from('account_members')
            .select(
              'role, account:accounts!account_members_account_id_fkey(id, account_number, name, type, phone, billing_address, city, client_tier, created_at)'
            )
            .eq('user_id', client.id)
            .eq('is_active', true),
          supabase
            .from('tickets')
            .select('id', { count: 'exact', head: true })
            .eq('client_id', client.id)
            .in('status', ['PENDING', 'ASSIGNED', 'EN_ROUTE', 'ON_SCENE', 'IN_PROGRESS']),
        ]);

        // PostgREST types embedded relations as arrays even on FK joins; in practice
        // account_members.account_id is a single FK so the array always has 0–1 items.
        type RawMembership = {
          role: string;
          account:
            | {
                id: string;
                account_number: string;
                name: string;
                type: string | null;
                phone: string | null;
                billing_address: string | null;
                city: string | null;
                client_tier: string | null;
                created_at: string | null;
              }
            | Array<{
                id: string;
                account_number: string;
                name: string;
                type: string | null;
                phone: string | null;
                billing_address: string | null;
                city: string | null;
                client_tier: string | null;
                created_at: string | null;
              }>
            | null;
        };
        const accounts: ClientAccountInfo[] = ((memberships.data ?? []) as unknown as RawMembership[])
          .map((m) => {
            const acc = Array.isArray(m.account) ? m.account[0] ?? null : m.account;
            if (!acc) return null;
            return {
              id: acc.id,
              account_number: acc.account_number,
              name: acc.name,
              type: acc.type,
              phone: acc.phone,
              billing_address: acc.billing_address,
              city: acc.city,
              role: m.role,
              client_tier: acc.client_tier,
              created_at: acc.created_at,
            };
          })
          .filter((a): a is ClientAccountInfo => a !== null);

        // Only commit if the operator hasn't moved on to a different client.
        if (get().selectedClient?.id !== client.id) return;
        set({
          selectedClientDetails: {
            open_alerts: openAlerts.count ?? 0,
            accounts,
          },
          selectedClientDetailsLoading: false,
        });
      } catch (err) {
        console.error('Error loading client details:', err);
        if (get().selectedClient?.id !== client.id) return;
        set({ selectedClientDetailsLoading: false });
      }
    })();
  },

  fetchClients: async (silent = false) => {
    if (!silent) set({ clientsLoading: true });
    try {
      const { data, error } = await supabase
        .from('client_locations')
        .select(`
          client_id,
          lat,
          lng,
          status,
          accuracy,
          speed,
          battery_level,
          is_charging,
          updated_at,
          client:profiles!client_locations_client_id_fkey(full_name, phone, marker_icon)
        `)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      // Same staleness override as patrols: if the device hasn't reported
      // within PRESENCE_FRESHNESS_MS treat it as OFFLINE so the operator
      // doesn't see clients permanently stuck on "En línea" after the app
      // is killed by the OS or loses connectivity without a clean logout.
      const nowMs = Date.now();
      const clients: ClientLive[] = (data || []).map((c) => {
        const updatedAt = c.updated_at as string;
        const ageMs = nowMs - new Date(updatedAt).getTime();
        const stale = ageMs > PRESENCE_FRESHNESS_MS;
        const storedStatus = c.status as ClientLive['status'];
        return {
          id: c.client_id,
          name: (c.client as { full_name?: string })?.full_name ?? 'Cliente',
          latitude: (c.lat as number) ?? 0,
          longitude: (c.lng as number) ?? 0,
          status: stale ? 'OFFLINE' : storedStatus,
          phone: (c.client as { phone?: string })?.phone,
          marker_icon: (c.client as { marker_icon?: string })?.marker_icon ?? 'initials',
          battery_level: (c.battery_level as number | null) ?? null,
          is_charging: (c.is_charging as boolean | null) ?? null,
          accuracy: (c.accuracy as number | null) ?? null,
          speed: (c.speed as number | null) ?? null,
          last_update: updatedAt,
        };
      });
      set({ clients, clientsLoading: false });
    } catch (error) {
      console.error('Error fetching live clients:', error);
      set({ clientsLoading: false });
    }
  },

  // ─── Sites + ARC events ──────────────────────────────────────────────────
  // Single round-trip: list every site (lat/lng known) plus every currently
  // open arc_event, joined to its CID code metadata in one query. The store
  // then derives the per-site aggregate (counts, max severity, dispatched
  // flag) so map markers and the right-panel queue stay in sync without
  // duplicate fetches. Tickets created via the panic auto-dispatch trigger
  // are already linked through arc_events.ticket_id, so we use that to
  // colour the marker blue when a patrol is en route.
  fetchSitesAndEvents: async (silent = false) => {
    if (!silent) set({ sitesLoading: true, arcEventsLoading: true });

    // Sites and events are fetched independently — a failure on one must NOT
    // wipe the other. Previously a single try/catch wrapped both queries and
    // any error in arc_events left the right-panel "Cuentas" tab empty even
    // though sites loaded fine. We now isolate the failures and log per query.
    //
    // Sites: full active roster. Most rows still lack lat/lng (HPP sync
    // imports them without coordinates), so we no longer require coords at
    // the query level — the map skips markers with null coords, while the
    // right-panel list shows the full account roster. We also tolerate rows
    // where is_active was never set (older imports) by treating null as true.
    let sitesData: Array<Record<string, unknown>> = [];
    try {
      const { data, error } = await supabase
        .from('alarm_sites')
        .select('id, name, address, account_number, account_id, lat, lng, is_active, account:accounts!alarm_sites_account_id_fkey(name, account_number, phone, type, city, client_tier, created_at)')
        .or('is_active.eq.true,is_active.is.null');
      if (error) throw error;
      sitesData = (data ?? []) as Array<Record<string, unknown>>;
    } catch (error) {
      console.error('[emergency-store] fetchSites failed:', error);
      set({ sitesLoading: false });
    }

    // Events: anything not yet resolved. Bounded LIMIT so a runaway panel
    // can't OOM the dashboard. Joined to alarm_sites (name/address/coords).
    // NOTE: no join to cid_codes — that FK was intentionally dropped in
    // migration 20260826000000 (AX PRO panels send proprietary CID codes not
    // present in the reference table; the join broke every /emergency load
    // with PGRST200 "no relationship found"). Description now comes from the
    // local getCidDescription() dictionary instead, same as EventRibbon/ArcEventsPage.
    let eventsData: unknown[] = [];
    try {
      const { data, error } = await supabase
        .from('arc_events')
        .select(`
          id, site_id, panel_id, cid_code, cid_qualifier, zone_no, zone_name,
          severity, occurred_at, received_at, acknowledged_at, resolved_at, ticket_id,
          site:alarm_sites!arc_events_site_id_fkey(name, address, lat, lng, account_id, account_number, account:accounts!alarm_sites_account_id_fkey(client_tier, created_at))
        `)
        .is('resolved_at', null)
        .order('received_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      eventsData = data ?? [];
    } catch (error) {
      console.error('[emergency-store] fetchArcEvents failed:', error);
      set({ arcEventsLoading: false });
    }

    try {

      type RawEvt = {
        id: string;
        site_id: string | null;
        panel_id: string | null;
        cid_code: string;
        cid_qualifier: 'E' | 'R';
        zone_no: number | null;
        zone_name: string | null;
        severity: 'INFO' | 'WARNING' | 'ALARM' | 'CRITICAL';
        occurred_at: string;
        received_at: string;
        acknowledged_at: string | null;
        resolved_at: string | null;
        ticket_id: string | null;
        site: { name: string; address: string | null; lat: number | null; lng: number | null; account_id: string | null; account_number: string | null; account: { client_tier: string | null; created_at: string | null } | { client_tier: string | null; created_at: string | null }[] | null } | { name: string; address: string | null; lat: number | null; lng: number | null; account_id: string | null; account_number: string | null; account: { client_tier: string | null; created_at: string | null } | { client_tier: string | null; created_at: string | null }[] | null }[] | null;
      };

      const events: ArcEventLite[] = ((eventsData ?? []) as unknown as RawEvt[]).map((e) => {
        const site = Array.isArray(e.site) ? e.site[0] ?? null : e.site;
        const acc = site ? (Array.isArray(site.account) ? site.account[0] ?? null : site.account) : null;
        return {
          id: e.id,
          site_id: e.site_id,
          panel_id: e.panel_id,
          cid_code: e.cid_code,
          cid_qualifier: e.cid_qualifier,
          zone_no: e.zone_no,
          zone_name: e.zone_name,
          severity: e.severity,
          received_at: e.received_at,
          occurred_at: e.occurred_at,
          acknowledged_at: e.acknowledged_at,
          resolved_at: e.resolved_at,
          ticket_id: e.ticket_id,
          description_es: getCidDescription(e.cid_code),
          auto_dispatch: false,
          site_name: site?.name ?? null,
          site_address: site?.address ?? null,
          site_lat: site?.lat ?? null,
          site_lng: site?.lng ?? null,
          account_id: site?.account_id ?? null,
          account_number: site?.account_number ?? null,
          account_tier: acc?.client_tier ?? null,
          account_created_at: acc?.created_at ?? null,
        };
      });

      // Aggregate events per site for marker colouring.
      const sevRank: Record<string, number> = { INFO: 1, WARNING: 2, ALARM: 3, CRITICAL: 4 };
      const perSite = new Map<string, { count: number; maxSev: number; dispatched: boolean }>();
      for (const ev of events) {
        if (!ev.site_id) continue;
        const cur = perSite.get(ev.site_id) ?? { count: 0, maxSev: 0, dispatched: false };
        cur.count += 1;
        cur.maxSev = Math.max(cur.maxSev, sevRank[ev.severity] ?? 0);
        if (ev.ticket_id) cur.dispatched = true;
        perSite.set(ev.site_id, cur);
      }

      const sevToTier = (n: number): SiteSeverity => {
        if (n >= 4) return 'critical';
        if (n >= 3) return 'alarm';
        if (n >= 2) return 'warning';
        if (n >= 1) return 'info';
        return 'idle';
      };

      const sites: SiteMarker[] = (sitesData ?? []).map((s) => {
        const agg = perSite.get(s.id as string) ?? { count: 0, maxSev: 0, dispatched: false };
        // Volume bump: a site with many simultaneous open events is treated
        // as priority (critical tier) even if individual events are warning.
        // 3+ open events on a single site is unusual and worth flagging.
        const baseTier = sevToTier(agg.maxSev);
        const tier: SiteSeverity = agg.dispatched
          ? 'dispatched'
          : agg.count >= 3
            ? 'critical'
            : baseTier;
        type AccountRel = {
          name?: string | null;
          account_number?: string | null;
          phone?: string | null;
          type?: string | null;
          city?: string | null;
          client_tier?: string | null;
          created_at?: string | null;
        };
        const rawRel = s.account as AccountRel | AccountRel[] | null | undefined;
        const accountObj: AccountRel | null = Array.isArray(rawRel) ? rawRel[0] ?? null : rawRel ?? null;
        const accountNumber = accountObj?.account_number ?? (s.account_number as string | null) ?? null;
        return {
          id: s.id as string,
          name: (s.name as string) ?? 'Sitio',
          address: (s.address as string | null) ?? null,
          account_number: accountNumber,
          account_id: (s.account_id as string | null) ?? null,
          account_name: accountObj?.name ?? null,
          account_phone: accountObj?.phone ?? null,
          account_type: accountObj?.type ?? null,
          account_city: accountObj?.city ?? null,
          account_tier: accountObj?.client_tier ?? null,
          account_created_at: accountObj?.created_at ?? null,
          latitude: s.lat as number,
          longitude: s.lng as number,
          open_events_count: agg.count,
          max_severity: tier,
          has_dispatched_ticket: agg.dispatched,
        };
      });

      set({
        sites,
        arcEvents: events,
        sitesLoading: false,
        arcEventsLoading: false,
      });
    } catch (error) {
      console.error('[emergency-store] fetchSitesAndEvents aggregation failed:', error);
      set({ sitesLoading: false, arcEventsLoading: false });
    }
  },

  selectSite: (site) => {
    set({
      selectedSite: site,
      selectedAlert: null,
      selectedPatrol: null,
      selectedClient: null,
      selectedArcEvent: null,
    });
  },

  selectArcEvent: (event) => {
    set({
      selectedArcEvent: event,
      selectedAlert: null,
      selectedPatrol: null,
      selectedClient: null,
      // Keep selectedSite so the operator sees site context behind the event
    });
  },

  acknowledgeArcEventInStore: async (eventId) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { error } = await supabase
      .from('arc_events')
      .update({ acknowledged_at: new Date().toISOString(), acknowledged_by: user.id })
      .eq('id', eventId);
    if (error) {
      console.error('[emergency-store] acknowledge failed:', error);
      return;
    }
    // Optimistic local update
    set((state) => ({
      arcEvents: state.arcEvents.map((e) =>
        e.id === eventId ? { ...e, acknowledged_at: new Date().toISOString() } : e
      ),
      selectedArcEvent:
        state.selectedArcEvent?.id === eventId
          ? { ...state.selectedArcEvent, acknowledged_at: new Date().toISOString() }
          : state.selectedArcEvent,
    }));
  },

  resolveArcEventInStore: async (eventId, resolution) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('arc_events')
      .update({
        acknowledged_at: now,
        acknowledged_by: user.id,
        resolved_at: now,
        resolved_by: user.id,
        resolution: resolution.trim() || null,
        response_started_at: now,
        response_completed_at: now,
      })
      .eq('id', eventId);
    if (error) {
      console.error('[emergency-store] resolve failed:', error);
      return;
    }
    // Drop from active list
    set((state) => ({
      arcEvents: state.arcEvents.filter((e) => e.id !== eventId),
      selectedArcEvent: state.selectedArcEvent?.id === eventId ? null : state.selectedArcEvent,
    }));
    // Refresh aggregates
    await get().fetchSitesAndEvents(true);
  },

  // Manual operator escalation: turn an arc_event into a ticket and assign
  // the nearest patrol. Mirrors what handle_panic_arc_event() does for
  // auto_dispatch CID codes — but invoked by the operator, so we use the
  // RLS policy "Operators can create panel tickets" added in migration
  // 20260507000009. Returns success=false with a reason if dispatch is not
  // possible (no site coords, no patrol in range, etc.).
  dispatchPatrolForArcEvent: async (eventId) => {
    const ev = get().arcEvents.find((e) => e.id === eventId);
    if (!ev) return { success: false, reason: 'Evento no encontrado' };
    if (!ev.site_id || ev.site_lat == null || ev.site_lng == null) {
      return { success: false, reason: 'El sitio no tiene coordenadas' };
    }
    if (ev.ticket_id) {
      return { success: false, reason: 'Ya existe un ticket para este evento' };
    }

    // Determine alert type based on CID code (Contact ID standard).
    // Fire/hazard: 110x, 115x, 1162 → FIRE
    // Panic/duress: 112x → PANIC
    // Burglar/intrusion: 113x, 114x → INTRUSION
    const fireCodes = ['1100','1101','1102','1110','1111','1112','1113','1114','1115','1116','1117','1118','1150','1151','1162'];
    const panicCodes = ['1120','1121','1122','1123'];
    const ticketType: AlertType = fireCodes.includes(ev.cid_code)
      ? 'FIRE'
      : panicCodes.includes(ev.cid_code)
      ? 'PANIC'
      : 'INTRUSION';

    const { data: ticket, error: insertErr } = await supabase
      .from('tickets')
      .insert({
        type: ticketType,
        priority: 'CRITICAL',
        status: 'PENDING',
        client_id: null,
        account_id: ev.account_id,
        site_id: ev.site_id,
        location: `SRID=4326;POINT(${ev.site_lng} ${ev.site_lat})`,
        lat: ev.site_lat,
        lng: ev.site_lng,
        address: ev.site_address,
        description: `${ev.description_es ?? `CID ${ev.cid_code}`} — ${ev.site_name ?? 'Sitio'}` +
          (ev.zone_no != null ? ` (Zona ${ev.zone_no})` : ''),
        source_arc_event_id: ev.id,
      })
      .select('id')
      .single();

    if (insertErr || !ticket) {
      console.error('[emergency-store] dispatch ticket insert failed:', insertErr);
      return { success: false, reason: insertErr?.message ?? 'No se pudo crear el ticket' };
    }

    // Link the event to the ticket and acknowledge it in the same write.
    const { data: { user } } = await supabase.auth.getUser();
    await supabase
      .from('arc_events')
      .update({
        ticket_id: ticket.id,
        acknowledged_at: ev.acknowledged_at ?? new Date().toISOString(),
        acknowledged_by: user?.id ?? null,
        response_started_at: new Date().toISOString(),
      })
      .eq('id', ev.id);

    // Auto-assign nearest patrol via the SECURITY DEFINER RPC. This already
    // applies the freshness filter (patrols updated <5min ago) so we won't
    // assign a phantom patrol.
    const { data: nearest } = await supabase.rpc('find_nearest_patrol', {
      alert_location: `SRID=4326;POINT(${ev.site_lng} ${ev.site_lat})`,
      max_distance_km: 15,
      excluded_patrol_ids: [],
      p_account_id: ev.account_id,
    });

    if (nearest && nearest.length > 0) {
      const patrol = nearest[0];
      await supabase
        .from('tickets')
        .update({
          patrol_id: patrol.patrol_id,
          status: 'ASSIGNED',
          assigned_at: new Date().toISOString(),
        })
        .eq('id', ticket.id);

      // Fire-and-forget FCM (same edge function as auto-reassign).
      supabase.functions
        .invoke('auto-assign-notify', {
          body: {
            ticket_id: ticket.id,
            patrol_id: patrol.patrol_id,
            ticket_type: ticketType,
            lat: ev.site_lat,
            lng: ev.site_lng,
            priority: 'CRITICAL',
          },
        })
        .catch((e) => console.warn('[emergency-store] auto-assign-notify failed:', e));
    }

    // Track quota usage / bill overage for the managed account's patrol package
    // (mirrors what handle_panic_arc_event() does for the automatic path).
    let billed = false;
    let invoiceNumber: string | undefined;
    let amount: number | undefined;
    try {
      const { data: billResult } = await supabase.rpc('bill_patrol_dispatch', { p_ticket_id: ticket.id });
      const r = billResult as { billed?: boolean; invoice_number?: string; amount?: number } | null;
      if (r?.billed) {
        billed = true;
        invoiceNumber = r.invoice_number;
        amount = r.amount;
      }
    } catch (e) {
      console.warn('[emergency-store] bill_patrol_dispatch failed:', e);
    }

    // Refresh both sides so the queue and map markers update.
    await get().fetchActiveAlerts(true);
    await get().fetchSitesAndEvents(true);

    return { success: true, billed, invoiceNumber, amount };
  },

  assignPatrolToAlert: async (alertId, patrolId) => {
    try {
      const { error } = await supabase
        .from('tickets')
        .update({
          patrol_id: patrolId,
          status: 'ASSIGNED' as TicketStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', alertId);

      if (error) throw error;

      // Update patrol status
      await supabase
        .from('patrol_locations')
        .update({ status: 'ASSIGNED' })
        .eq('patrol_id', patrolId);

      // Push an FCM notification to the patrol's phone so they get a tray
      // toast even if the app is in the background. Best-effort: never block
      // the UI flow on this — the assignment itself already succeeded.
      supabase.functions
        .invoke('notify-patrol-assignment', { body: { ticket_id: alertId } })
        .catch((e) => console.warn('[emergency-store] notify-patrol-assignment failed:', e));

      // Refresh data
      await Promise.all([get().fetchActiveAlerts(), get().fetchPatrols()]);
    } catch (error) {
      console.error('Error assigning patrol:', error);
    }
  },

  updateAlertStatus: async (alertId, status, notes) => {
    try {
      const updateData: Record<string, unknown> = {
        status,
        updated_at: new Date().toISOString(),
      };
      if (notes) {
        updateData.resolution_notes = notes;
      }
      if (status === 'RESOLVED' || status === 'CLOSED') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase.from('tickets').update(updateData).eq('id', alertId);

      if (error) throw error;

      // Refresh data
      await get().fetchActiveAlerts();
    } catch (error) {
      console.error('Error updating alert status:', error);
    }
  },

  // Mass update used by the "procesar lote" action in the queue when a single
  // alarm panel triggers a flood of panic/intrusion tickets that the operator
  // wants to resolve or cancel together (false alarm, system test, etc.).
  // Single round-trip via .in('id', ids) so it stays cheap even for 50+ ids.
  bulkUpdateAlertStatus: async (alertIds, status, notes) => {
    if (alertIds.length === 0) return { updated: 0 };
    try {
      const updateData: Record<string, unknown> = {
        status,
        updated_at: new Date().toISOString(),
      };
      if (notes) updateData.resolution_notes = notes;
      if (status === 'RESOLVED' || status === 'CLOSED' || status === 'CANCELLED') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { error, count } = await supabase
        .from('tickets')
        .update(updateData, { count: 'exact' })
        .in('id', alertIds);

      if (error) throw error;

      await get().fetchActiveAlerts();
      return { updated: count ?? alertIds.length };
    } catch (error) {
      console.error('Error bulk updating alerts:', error);
      return { updated: 0 };
    }
  },

  subscribeToRealtime: () => {
    // Subscribe to ticket changes
    const ticketChannel = supabase
      .channel('arc-tickets')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tickets' },
        () => {
          console.log('[ARC realtime] tickets event → refetch');
          get().fetchActiveAlerts(true);
        }
      )
      .subscribe((status) => {
        console.log('[ARC realtime] tickets channel status:', status);
      });

    // Subscribe to patrol location changes
    const patrolChannel = supabase
      .channel('arc-patrols')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'patrol_locations' },
        () => {
          console.log('[ARC realtime] patrol_locations event → refetch');
          get().fetchPatrols(true);
        }
      )
      .subscribe((status) => {
        console.log('[ARC realtime] patrols channel status:', status);
      });

    // Subscribe to live client locations
    const clientChannel = supabase
      .channel('arc-clients')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'client_locations' },
        () => {
          console.log('[ARC realtime] client_locations event → refetch');
          get().fetchClients(true);
        }
      )
      .subscribe((status) => {
        console.log('[ARC realtime] clients channel status:', status);
      });

    // Subscribe to profile changes so the operator sees marker_icon edits
    // (and other profile changes) without needing to reload the dashboard.
    const profilesChannel = supabase
      .channel('arc-profiles')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles' },
        () => {
          console.log('[ARC realtime] profiles event → refetch patrols + clients');
          get().fetchPatrols(true);
          get().fetchClients(true);
        }
      )
      .subscribe((status) => {
        console.log('[ARC realtime] profiles channel status:', status);
      });

    // Subscribe to ARC events: any insert/update/delete refreshes the
    // sites + queue. The trigger handle_panic_arc_event() may also create
    // a ticket, but that fires the tickets channel separately.
    const arcChannel = supabase
      .channel('arc-arc-events')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'arc_events' },
        () => {
          console.log('[ARC realtime] arc_events event → refetch sites');
          get().fetchSitesAndEvents(true);
        }
      )
      .subscribe((status) => {
        console.log('[ARC realtime] arc_events channel status:', status);
      });

    // Subscribe to alarm_sites so newly registered sites and edits to
    // existing ones (lat/lng changes, deactivations) appear without a
    // page reload.
    const sitesChannel = supabase
      .channel('arc-alarm-sites')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'alarm_sites' },
        () => {
          console.log('[ARC realtime] alarm_sites event → refetch sites');
          get().fetchSitesAndEvents(true);
        }
      )
      .subscribe((status) => {
        console.log('[ARC realtime] alarm_sites channel status:', status);
      });

    set({
      unsubscribe: () => {
        supabase.removeChannel(ticketChannel);
        supabase.removeChannel(patrolChannel);
        supabase.removeChannel(clientChannel);
        supabase.removeChannel(profilesChannel);
        supabase.removeChannel(arcChannel);
        supabase.removeChannel(sitesChannel);
      },
    });
  },

  reset: () => {
    const { unsubscribe } = get();
    if (unsubscribe) unsubscribe();
    set({
      activeAlerts: [],
      selectedAlert: null,
      patrols: [],
      selectedPatrol: null,
      clients: [],
      selectedClient: null,
      sites: [],
      selectedSite: null,
      arcEvents: [],
      selectedArcEvent: null,
      unsubscribe: null,
    });
  },
}));

// Helper to get alert type color
export function getAlertTypeColor(type: AlertType): string {
  switch (type) {
    case 'SOS':
      return '#ef4444'; // red
    case 'FIRE':
      return '#f97316'; // orange
    case 'MEDICAL':
      return '#ec4899'; // pink
    case 'INTRUSION':
      return '#8b5cf6'; // purple
    case 'PANIC':
      return '#dc2626'; // dark red
    default:
      return '#6b7280'; // gray
  }
}

// Helper to get status badge style
export function getStatusColor(status: TicketStatus): { bg: string; text: string } {
  switch (status) {
    case 'PENDING':
      return { bg: 'bg-red-500/20', text: 'text-red-400' };
    case 'ASSIGNED':
      return { bg: 'bg-amber-500/20', text: 'text-amber-400' };
    case 'EN_ROUTE':
      return { bg: 'bg-blue-500/20', text: 'text-blue-400' };
    case 'ON_SCENE':
      return { bg: 'bg-green-500/20', text: 'text-green-400' };
    case 'IN_PROGRESS':
      return { bg: 'bg-purple-500/20', text: 'text-purple-400' };
    case 'RESOLVED':
      return { bg: 'bg-emerald-500/20', text: 'text-emerald-400' };
    default:
      return { bg: 'bg-slate-500/20', text: 'text-slate-400' };
  }
}
